import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingTabDefinition } from '@/types/leveling'
import { EditableText } from 'components/editor/EditableText'
import React from 'react'
import { useDrag, useDrop } from 'react-dnd'
import './leveling-tab-item.scss'

let timeout = false

export function findTab(id: string, embed: LevelingEmbed) {
  const tabIndex = embed.tabs.findIndex((item) => item.id === id)
  if (tabIndex !== -1) return ['tabs', tabIndex, embed.tabs[tabIndex]] as const

  const extraTabIndex = embed.extraTabs.findIndex((item) => item.id === id)
  if (extraTabIndex !== -1)
    return ['extraTabs', extraTabIndex, embed.extraTabs[extraTabIndex]] as const

  return [undefined, undefined, undefined] as const
}

export function LevelingTabItem({
  embed,
  onEmbedChange,
  tab,
  setTab,
  tabsAttr,
  tabIndex
}: {
  embed: LevelingEmbed
  onEmbedChange: (value: CallbackWrap<Embed>) => void
  tab: LevelingTabDefinition
  setTab: (id: string) => void
  tabsAttr: 'tabs' | 'extraTabs'
  tabIndex: number
}) {
  const updateOrder = (dragId: string, hoverId: string) =>
    onEmbedChange((embed) => {
      if (embed.type !== 'leveling') return embed

      const [dragAttr, dragIndex, draggedItem] = findTab(dragId, embed)
      const [hoverAttr, hoverIndex, hoveredItem] = findTab(hoverId, embed)
      if (!draggedItem || !hoveredItem) return embed

      embed = { ...embed }
      if (dragAttr === hoverAttr) {
        embed[dragAttr] = embed[dragAttr]!.slice()
        embed[dragAttr]![dragIndex] = hoveredItem
        embed[dragAttr]![hoverIndex] = draggedItem
      } else {
        embed[dragAttr] = embed[dragAttr]!.slice()
        embed[dragAttr]!.splice(dragIndex, 1)
        embed[hoverAttr] = embed[hoverAttr]!.slice()
        embed[hoverAttr]!.splice(hoverIndex, 0, draggedItem)
      }
      return embed
    })

  const mountedRef = React.useRef(true)
  React.useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const ref = React.useRef<HTMLSpanElement>(null)
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'poe-leveling-tab-item',
      item: () => {
        return { id: tab.id, index: tabIndex }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [tab, tabIndex]
  )

  const [{ handlerId }, drop] = useDrop<
    { id: string },
    undefined,
    { handlerId: string | symbol | null }
  >(
    () => ({
      accept: 'poe-leveling-tab-item',
      collect(monitor) {
        return { handlerId: monitor.getHandlerId() }
      },
      hover(dragItem: { id: string }) {
        if (!ref.current || dragItem.id === tab.id) {
          return
        }

        // This requires a timeout since too many updates can break it
        if (!timeout) {
          timeout = true
          updateOrder(dragItem.id, tab.id)
          setTimeout(() => {
            timeout = false
          }, 100)
        }
      }
    }),
    [tab.id]
  )

  drag(drop(ref))

  return (
    <span ref={ref} className={'poe-leveling-tab-item'}>
      {['overview', 'leveling', 'progression'].includes(tab.id) ? (
        <EditableText
          value={tab.name}
          onChange={(newValue) => {
            onEmbedChange((embed) => {
              if (embed.type !== 'leveling') return embed
              embed = { ...embed }
              embed[tabsAttr] = embed[tabsAttr]!.slice()
              embed[tabsAttr]![tabIndex] = {
                ...embed[tabsAttr]![tabIndex],
                name: newValue
              }
              return embed
            })
          }}
        />
      ) : (
        <>
          <EditableText
            value={tab.name}
            onChange={(newValue) => {
              onEmbedChange((embed) => {
                if (embed.type !== 'leveling') return embed
                embed = { ...embed }
                embed[tabsAttr] = embed[tabsAttr]!.slice()
                embed[tabsAttr]![tabIndex] = {
                  ...embed[tabsAttr]![tabIndex],
                  name: newValue
                }
                return embed
              })
            }}
          />
          <button
            style={{ height: '20px', lineHeight: '1' }}
            onClick={() => {
              if (
                window.confirm('Are you sure you want to delete this?') === true
              ) {
                onEmbedChange((embed) => {
                  if (embed.type !== 'leveling') return embed
                  embed = { ...embed }
                  embed[tabsAttr] = embed[tabsAttr]!.slice()
                  embed[tabsAttr]!.splice(tabIndex, 1)
                  return embed
                })
                setTimeout(() => {
                  if (mountedRef.current && embed.tabs.length)
                    setTab(embed.tabs[0].id)
                }, 100)
              }
            }}>
            Delete
          </button>
        </>
      )}
    </span>
  )
}
